import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { StyledDatePickerContainer } from "../../styles/DatePicker";
import { InputIcon } from '../../styles/PersonalInfoStep';
import CalendarMonth from '@mui/icons-material/CalendarMonth';

interface DatePickerProps {
  /** The label to display for the date picker */
  label: string;
  /** The selected date */
  value?: Dayjs | string | null;
  /** Whether the date picker is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
  /** Margin right for the calendar icon */
  marginRight?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Additional class name */
  className?: string;
  /** Callback for date selection */
  onChange: (date: Dayjs | null) => void;
  /** Type of the date picker */
  type?: string;
  /** Disable future dates (dates after today) */
  disableFuture?: boolean;
  /** Disable past dates (dates before today) */
  disablePast?: boolean;
  /** Custom minimum date */
  minDate?: Dayjs | string;
  /** Custom maximum date */
  maxDate?: Dayjs | string;
  /** Custom date validation function */
  shouldDisableDate?: (date: Dayjs) => boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  onChange,
  value = null,
  disabled = false,
  error,
  marginRight,
  required = false,
  className = "",
  type,
  disableFuture = false,
  disablePast = false,
  minDate,
  maxDate,
  shouldDisableDate,
}) => {
  const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(
    value ? dayjs(value) : null
  );

  React.useEffect(() => {
    setSelectedDate(value ? dayjs(value) : null);
  }, [value]);

  const handleChange = (newValue: Dayjs | null) => {
    if (!newValue || dayjs(newValue).isValid()) {
      setSelectedDate(newValue);
      if (onChange) {
        onChange(newValue);
      }
    }
  };

  // Calculate min and max dates based on props
  const getMinDate = (): Dayjs | undefined => {
    if (minDate) {
      return dayjs(minDate);
    }
    if (disablePast) {
      return dayjs(); // Today
    }
    return undefined;
  };

  const getMaxDate = (): Dayjs | undefined => {
    if (maxDate) {
      return dayjs(maxDate);
    }
    if (disableFuture) {
      return dayjs(); // Today
    }
    return undefined;
  };

  // Custom date validation function
  const customShouldDisableDate = (date: Dayjs): boolean => {
    // If custom shouldDisableDate is provided, use it
    if (shouldDisableDate) {
      return shouldDisableDate(date);
    }

    const today = dayjs();
    
    // Disable future dates if disableFuture is true
    if (disableFuture && date.isAfter(today, 'day')) {
      return true;
    }
    
    // Disable past dates if disablePast is true
    if (disablePast && date.isBefore(today, 'day')) {
      return true;
    }
    
    // Check custom min/max dates
    if (minDate && date.isBefore(dayjs(minDate), 'day')) {
      return true;
    }
    
    if (maxDate && date.isAfter(dayjs(maxDate), 'day')) {
      return true;
    }
    
    return false;
  };

  return (
    <StyledDatePickerContainer className={className} $marginRight={marginRight}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDatePicker
          slots={{
            openPickerIcon: () => (
              <InputIcon>
                <CalendarMonth fontSize="medium" />
              </InputIcon>
            ),
          }}
          label={label}
          value={selectedDate}
          onChange={handleChange}
          disabled={disabled}
          format="DD/MM/YYYY"
          minDate={getMinDate()}
          maxDate={getMaxDate()}
          shouldDisableDate={customShouldDisableDate}
          slotProps={{
            textField: {
              variant: 'standard',
              fullWidth: true,
              error: !!error,
              helperText: error,
              required,
              InputProps: {
                disableUnderline: true,
              },
              InputLabelProps: {
                shrink: true,
                sx: {
                  fontWeight: 600,
                  color: '#E39435',
                  fontSize: "12px",
                  '&.Mui-focused': {
                    color: '#E39435',
                  },
                  
                },
              },
              inputProps: {
                placeholder: 'DD/MM/YYYY',
                autoComplete: 'off',
              },
              sx: {
                mt: '8px',
                
                '& .MuiInputBase-input': {
                  color: '#1E2F23',
                  fontSize: '14px',
                  fontWeight: 600,
                  padding: '8px 0',
                },
                '& .MuiSvgIcon-root': {
                  color: '#e39435',
                  marginTop: '-4px',
                  marginRight: marginRight || '8px',
                },
                '& .MuiIconButton-root': {
                  background: 'transparent',
                  marginTop: '-24px',
                  marginRight: '-18px',
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </StyledDatePickerContainer>
  );
};

export default DatePicker;

// Usage Examples:
/*
// 1. Basic usage - no restrictions
<DatePicker
  label="Select Date"
  value={selectedDate}
  onChange={setSelectedDate}
/>

// 2. Disable future dates (only allow today and past dates)
<DatePicker
  label="Birth Date"
  value={birthDate}
  onChange={setBirthDate}
  disableFuture={true}
/>

// 3. Disable past dates (only allow today and future dates)
<DatePicker
  label="Appointment Date"
  value={appointmentDate}
  onChange={setAppointmentDate}
  disablePast={true}
/>

// 4. Custom date range with specific min/max dates
<DatePicker
  label="Event Date"
  value={eventDate}
  onChange={setEventDate}
  minDate="2024-01-01"
  maxDate="2024-12-31"
/>

// 5. Custom date validation (e.g., disable weekends)
<DatePicker
  label="Business Date"
  value={businessDate}
  onChange={setBusinessDate}
  shouldDisableDate={(date) => {
    // Disable weekends (Saturday = 6, Sunday = 0)
    const day = date.day();
    return day === 0 || day === 6;
  }}
/>

// 6. Combine multiple restrictions
<DatePicker
  label="License Expiry Date"
  value={expiryDate}
  onChange={setExpiryDate}
  disablePast={true}
  maxDate={dayjs().add(5, 'year')}
/>

// 7. Complex validation with custom logic
<DatePicker
  label="Delivery Date"
  value={deliveryDate}
  onChange={setDeliveryDate}
  shouldDisableDate={(date) => {
    // Disable past dates
    if (date.isBefore(dayjs(), 'day')) return true;
    
    // Disable weekends
    const day = date.day();
    if (day === 0 || day === 6) return true;
    
    // Disable specific holidays
    const holidays = ['2024-12-25', '2024-01-01'];
    return holidays.some(holiday => date.isSame(dayjs(holiday), 'day'));
  }}
/>
*/